import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { SectionCard } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { useAuth, hasPermission } from "../../context/AuthContext";

const ACTIVITIES = [
  { value: "PACKING", label: "Packing" },
  { value: "STACKING", label: "Stacking" },
  { value: "TRUCK_LOADING", label: "Truck loading" },
  { value: "DIRECT_LOADING", label: "Direct loading" },
  { value: "CUTTING_REPROCESS", label: "Reprocess cutting" },
];

const MOVE_LABELS = {
  PACKING_TO_STACK: "Packing to stack",
  DIRECT_PACKING_TO_TRUCK: "Direct packing to truck",
  STACK_TO_TRUCK: "Stack to truck",
  STACK_TO_REPROCESS_CUT: "Stack to reprocess cut",
};

function inr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function statusLabel(status) {
  if (status === "SYNCED_TO_TALLY") return "Approved & synced to Tally";
  if (status === "MANAGER_APPROVED") return "Approved · Tally agent not connected";
  if (status === "REJECTED") return "Rejected";
  return "Awaiting manager approval";
}

export default function DailyLabourSettlement({ data, contractors, onReload }) {
  const { user } = useAuth();
  const canSettle = hasPermission(user, "page.accounts") || hasPermission(user, "labour.contract.manage") || hasPermission(user, "page.labour_warehouse");
  const canApprove = hasPermission(user, "tally.sync.approve");
  const canRates = hasPermission(user, "labour.contract.manage") || hasPermission(user, "page.accounts");

  const [contractorId, setContractorId] = useState("");
  const [workDate, setWorkDate] = useState(todayIso());
  const [contractorType, setContractorType] = useState("PACKING");
  const [summary, setSummary] = useState(null);
  const [cash, setCash] = useState("");
  const [penalty, setPenalty] = useState("");
  const [penaltyReason, setPenaltyReason] = useState("");
  const [other, setOther] = useState("");
  const [applyTds, setApplyTds] = useState(false);
  const [tdsRate, setTdsRate] = useState("1");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState({
    contractor_id: "",
    activity_type: "PACKING",
    pack_size_kg: "50",
    rate_per_bag: "",
    effective_from: todayIso(),
  });

  useEffect(() => {
    if (!contractorId || !workDate) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    api.get("/labour/settlements/unsettled-summary", { params: { contractor_id: contractorId, work_date: workDate } })
      .then(({ data: body }) => {
        if (!cancelled) setSummary(body);
      })
      .catch((err) => {
        if (!cancelled) {
          setSummary(null);
          toast.error(extractError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [contractorId, workDate, data?.vouchers]);

  const lines = useMemo(() => {
    if (!summary) return [];
    if (contractorType === "PACKING") {
      return (summary.packing?.lines || []).map((row) => ({
        id: `p-${row.id}`,
        activity: "Packing",
        detail: `${row.item_name || "Item"} · ${row.pack_size_kg} kg · ${row.production_batch_code || ""}`,
        bags: row.approved_bags_count,
        rate: row.rate_per_bag,
        amount: row.line_amount,
        status: row.status,
      }));
    }
    return (summary.movements?.lines || []).map((row) => ({
      id: `m-${row.id}`,
      activity: MOVE_LABELS[row.movement_type] || row.movement_type,
      detail: row.stack_label || `${row.pack_size_kg} kg`,
      bags: row.bags_count,
      rate: row.rate_per_bag,
      amount: row.total_amount,
      status: "VERIFIED",
    }));
  }, [summary, contractorType]);

  const gross = contractorType === "PACKING" ? Number(summary?.packing?.gross_amount || 0) : Number(summary?.movements?.gross_amount || 0);
  const cashN = Number(cash || 0);
  const penaltyN = Number(penalty || 0);
  const otherN = Number(other || 0);
  const tds = applyTds ? Math.round((gross * Number(tdsRate) / 100) * 100) / 100 : 0;
  const net = Math.round((gross - cashN - penaltyN - otherN - tds) * 100) / 100;
  const verifiedOnly = lines.every((row) => row.status === "APPROVED" || row.status === "PARTIALLY_APPROVED" || row.status === "VERIFIED");
  const canGenerate = canSettle && lines.length > 0 && gross > 0 && net >= 0 && verifiedOnly && (penaltyN <= 0 || penaltyReason.trim());

  const generate = async () => {
    setBusy(true);
    try {
      const { data: voucher } = await api.post("/labour/settlements/generate", {
        contractor_id: Number(contractorId),
        work_date: workDate,
        contractor_type: contractorType,
        cash_advances: cashN,
        penalties: penaltyN,
        penalty_reason: penaltyReason,
        other_deductions: otherN,
        apply_tds_194c: applyTds,
        tds_rate_pct: Number(tdsRate),
        remarks,
      });
      toast.success(`${voucher.voucher_number} is awaiting manager approval.`);
      setCash("");
      setPenalty("");
      setPenaltyReason("");
      setOther("");
      setRemarks("");
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      const { data: voucher } = await api.post(`/labour/settlements/${id}/approve-and-sync`);
      toast.success(
        voucher.approval_status === "SYNCED_TO_TALLY"
          ? `${voucher.voucher_number} synced to Tally.`
          : `${voucher.voucher_number} approved. The Tally agent was not connected, so the journal is waiting.`,
      );
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveRate = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/labour/rate-cards", {
        contractor_id: Number(rate.contractor_id),
        activity_type: rate.activity_type,
        pack_size_kg: Number(rate.pack_size_kg),
        rate_per_bag: Number(rate.rate_per_bag),
        effective_from: rate.effective_from,
      });
      toast.success("Piece rate saved.");
      setRate((r) => ({ ...r, rate_per_bag: "" }));
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Day and contractor" subtitle="Only verified packing and movement jobs for this date are included. Pending and rejected claims stay off the voucher.">
        <div className="grid gap-3 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Contractor</Label>
            <Select value={contractorId || undefined} onValueChange={setContractorId}>
              <SelectTrigger><SelectValue placeholder="Select contractor" /></SelectTrigger>
              <SelectContent>
                {contractors.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Contractor type</Label>
            <Select value={contractorType} onValueChange={setContractorType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PACKING">Packing</SelectItem>
                <SelectItem value="LOADING_STACKING">Stack & loading</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Verified jobs for the day">
        {lines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No verified unsettled jobs for this contractor and date.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-foreground">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Activity</th>
                  <th className="py-2 pr-3 font-medium">Detail</th>
                  <th className="py-2 pr-3 font-medium">Bags</th>
                  <th className="py-2 pr-3 font-medium">Rate</th>
                  <th className="py-2 font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((row) => (
                  <tr key={row.id} className="border-b border-border">
                    <td className="py-2 pr-3">{row.activity}</td>
                    <td className="py-2 pr-3">{row.detail}</td>
                    <td className="py-2 pr-3">{Number(row.bags || 0).toLocaleString("en-IN")}</td>
                    <td className="py-2 pr-3">{inr(row.rate)}</td>
                    <td className="py-2">{inr(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Daily adjustment" subtitle="Net due is gross approved earnings minus cash kharcha, penalties, other deductions, and TDS 194C.">
        <div className="space-y-3 max-w-xl">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Gross approved labour earnings</span>
            <span className="font-medium text-foreground">{inr(gross)}</span>
          </div>
          <div className="space-y-1.5">
            <Label>(−) Daily cash kharcha / advance</Label>
            <Input type="number" min="0" step="0.01" value={cash} onChange={(e) => setCash(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>(−) Quality / stitching penalty</Label>
            <Input type="number" min="0" step="0.01" value={penalty} onChange={(e) => setPenalty(e.target.value)} />
            <Input value={penaltyReason} onChange={(e) => setPenaltyReason(e.target.value)} placeholder="Penalty reason" />
          </div>
          <div className="space-y-1.5">
            <Label>(−) Mess, canteen, electric, or other</Label>
            <Input type="number" min="0" step="0.01" value={other} onChange={(e) => setOther(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2 text-foreground">
              <input type="checkbox" checked={applyTds} onChange={(e) => setApplyTds(e.target.checked)} />
              TDS 194C
            </label>
            <Select value={tdsRate} onValueChange={setTdsRate} disabled={!applyTds}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1% individual / HUF</SelectItem>
                <SelectItem value="2">2% company / partnership</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">{inr(tds)}</span>
          </div>
          <div className="space-y-1.5">
            <Label>Remarks</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          <div className="rounded-lg border border-border bg-card px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Net amount payable to contractor</span>
            <span className={`text-lg font-semibold ${net < 0 ? "text-destructive" : "text-primary"}`}>{inr(Math.max(net, 0))}</span>
          </div>
          {net < 0 && <p className="text-sm text-destructive">Deductions, advances, and TDS cannot exceed the gross approved amount.</p>}
          <Button type="button" disabled={busy || !canGenerate} onClick={generate}>Generate daily settlement voucher</Button>
        </div>
      </SectionCard>

      <SectionCard title="Settlement vouchers">
        {(data?.vouchers || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No labour settlement vouchers yet.</p>
        ) : (
          <div className="space-y-2">
            {data.vouchers.map((voucher) => (
              <div key={voucher.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border bg-card px-3 py-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{voucher.voucher_number} · {voucher.contractor_name}</p>
                  <p className="text-xs text-muted-foreground">{voucher.work_date} · {voucher.contractor_type} · net {inr(voucher.net_payable_amount)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide rounded-full border border-border px-2 py-1 text-foreground">
                    {statusLabel(voucher.approval_status)}
                  </span>
                  {canApprove && (voucher.approval_status === "DRAFT" || voucher.approval_status === "MANAGER_APPROVED") && (
                    <Button type="button" size="sm" disabled={busy} onClick={() => approve(voucher.id)}>Approve & sync</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {canRates && (
        <SectionCard title="Piece-rate card" subtitle="Verification and movements use the rate in force on the work date.">
          <form className="grid gap-3 md:grid-cols-3" onSubmit={saveRate}>
            <div className="space-y-1.5">
              <Label>Contractor</Label>
              <Select value={rate.contractor_id || undefined} onValueChange={(v) => setRate((r) => ({ ...r, contractor_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>
                  {contractors.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Activity</Label>
              <Select value={rate.activity_type} onValueChange={(v) => setRate((r) => ({ ...r, activity_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITIES.map((row) => (
                    <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Bag size (kg)</Label>
              <Input type="number" min="1" step="0.01" value={rate.pack_size_kg} onChange={(e) => setRate((r) => ({ ...r, pack_size_kg: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Rate per bag</Label>
              <Input type="number" min="0.01" step="0.01" value={rate.rate_per_bag} onChange={(e) => setRate((r) => ({ ...r, rate_per_bag: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label>Effective from</Label>
              <Input type="date" value={rate.effective_from} onChange={(e) => setRate((r) => ({ ...r, effective_from: e.target.value }))} required />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={busy}>Save rate</Button>
            </div>
          </form>
        </SectionCard>
      )}
    </div>
  );
}
